import React, { useState, ChangeEvent, FormEvent } from "react";
import { Grid, TextField, Button } from "@mui/material";
import SimpleReactValidator from "simple-react-validator";
import { toast } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";

interface SignUpForm {
  email: string;
  full_name: string;
  password: string;
  confirm_password: string;
}

const SignUpPage: React.FC = () => {
  const navigate = useNavigate();

  const [value, setValue] = useState<SignUpForm>({
    email: "",
    full_name: "",
    password: "",
    confirm_password: "",
  });

  const [validator] = useState(
    new SimpleReactValidator({
      className: "errorMessage",
    })
  );

  const changeHandler = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    setValue({ ...value, [e.target.name]: e.target.value });
    validator.showMessages();
  };

  const submitForm = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();

    if (validator.allValid()) {
      if (value.password !== value.confirm_password) {
        toast.error("Passwords do not match!");
        return;
      }

      setValue({
        email: "",
        full_name: "",
        password: "",
        confirm_password: "",
      });

      validator.hideMessages();
      toast.success("Registration completed successfully!");
      navigate("/login");
    } else {
      validator.showMessages();
      toast.error("All fields are required!");
    }
  };

  return (
    <Grid className="loginWrapper">
      <Grid className="loginForm">
        <h2>Signup</h2>
        <p>Signup your account</p>

        <form onSubmit={submitForm} noValidate>
          <Grid container spacing={3}>
            {/* Full Name */}
            <Grid item xs={12}>
              <TextField
                className="inputOutline"
                fullWidth
                placeholder="Full Name"
                value={value.full_name}
                variant="outlined"
                name="full_name"
                label="Name"
                InputLabelProps={{ shrink: true }}
                onBlur={changeHandler}
                onChange={changeHandler}
              />
              {validator.message("full name", value.full_name, "required|alpha")}
            </Grid>

            {/* Email */}
            <Grid item xs={12}>
              <TextField
                className="inputOutline"
                fullWidth
                placeholder="E-mail"
                value={value.email}
                variant="outlined"
                name="email"
                label="E-mail"
                InputLabelProps={{ shrink: true }}
                onBlur={changeHandler}
                onChange={changeHandler}
              />
              {validator.message("email", value.email, "required|email")}
            </Grid>

            {/* Password */}
            <Grid item xs={12}>
              <TextField
                className="inputOutline"
                fullWidth
                placeholder="Password"
                value={value.password}
                variant="outlined"
                name="password"
                type="password"
                label="Password"
                InputLabelProps={{ shrink: true }}
                onBlur={changeHandler}
                onChange={changeHandler}
              />
              {validator.message("password", value.password, "required")}
            </Grid>

            {/* Confirm Password */}
            <Grid item xs={12}>
              <TextField
                className="inputOutline"
                fullWidth
                placeholder="Confirm Password"
                value={value.confirm_password}
                variant="outlined"
                name="confirm_password"
                type="password"
                label="Confirm Password"
                InputLabelProps={{ shrink: true }}
                onBlur={changeHandler}
                onChange={changeHandler}
              />
              {validator.message(
                "confirm password",
                value.confirm_password,
                `required|in:${value.password}`
              )}
            </Grid>

            {/* Footer */}
            <Grid item xs={12}>
              <Grid className="formFooter">
                <Button
                  fullWidth
                  className="cBtn cBtnLarge cBtnTheme"
                  type="submit"
                >
                  Sign Up
                </Button>
              </Grid>

              <Grid className="loginWithSocial">
                <Button
                  className="facebook"
                  aria-label="Sign up with Facebook"
                >
                  <i className="fa fa-facebook"></i>
                </Button>
                <Button className="twitter" aria-label="Sign up with Twitter">
                  <i className="fa fa-twitter"></i>
                </Button>
                <Button className="linkedin" aria-label="Sign up with LinkedIn">
                  <i className="fa fa-linkedin"></i>
                </Button>
              </Grid>

              <p className="noteHelp">
                Already have an account?{" "}
                <Link to="/login">Return to Sign In</Link>
              </p>
            </Grid>
          </Grid>
        </form>

        <div className="shape-img">
          <i className="fi flaticon-honeycomb"></i>
        </div>
      </Grid>
    </Grid>
  );
};

export default SignUpPage;
